// useWebAccountsScope — what kinds of WhatsApp accounts a space has, and whether a QR account's
// bridge answers. Feeds the space-level alarm so it judges each account by its own signal.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWhatsAppWebSessions } from '@/hooks/useWhatsAppWebSessions';
import { useNow } from '@/hooks/useNow';
import { effectiveWebStatus } from '@/lib/whatsapp-web-api';
import type { WebAccountsScope } from '@/lib/whatsapp-web-signals';

export function useWebAccountsScope(tenantId: string | null | undefined): WebAccountsScope & { known: boolean; reload: () => Promise<void> } {
  const { sessions, loaded, reload: reloadSessions } = useWhatsAppWebSessions(tenantId);
  const [activeIds, setActiveIds] = useState<string[] | null>(null);
  const now = useNow();

  const loadActive = useCallback(async () => {
    if (!tenantId) return;
    const { data, error } = await supabase
      .from('api_configurations')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('api_provider', 'whatsapp')
      .eq('is_active', true);
    if (!error) setActiveIds(((data as Array<{ id: string }> | null) ?? []).map((row) => row.id));
  }, [tenantId]);

  useEffect(() => {
    void loadActive();
  }, [loadActive]);

  const webIds = new Set(sessions.map((session) => session.api_configuration_id));
  const active = new Set(activeIds ?? []);
  const activeSessions = sessions.filter((session) => active.has(session.api_configuration_id));

  return {
    known: !!tenantId && loaded && activeIds !== null,
    hasMetaAccounts: (activeIds ?? []).some((id) => !webIds.has(id)),
    bridgeDown: activeSessions.some((session) => effectiveWebStatus(session, now) === 'bridge_down'),
    reload: async () => {
      await Promise.all([reloadSessions(), loadActive()]);
    },
  };
}
